
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { useNavigate } from 'react-router-dom';
import { SubmitButton } from './form/SubmitButton';
import { SurpriseMeButton } from './form/SurpriseMeButton';
import { TripDetailsForm } from './form/TripDetailsForm';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';

const ItinerarySchema = z.object({
  preferences: z.string().min(10, 'Please describe your preferences in more detail.'),
  skillLevel: z.enum(['beginner', 'intermediate', 'advanced']),
  duration: z.coerce.number().min(1, 'Duration must be at least 1 day.').max(14, 'Duration cannot exceed 14 days.'),
  location: z.string().min(3, 'Please specify a location or region.'),
  date: z.date({ required_error: 'A date is required.' }),
});

export type ItineraryFormValues = z.infer<typeof ItinerarySchema>;

const surpriseValues: ItineraryFormValues = {
  preferences: 'A mix of iconic spots and hidden gems. Looking for beautiful scenery and maybe some coastal swimming. Not afraid of a short hike to a good spot.',
  skillLevel: 'intermediate',
  duration: 4,
  location: 'Isle of Skye',
  date: new Date(new Date().setDate(new Date().getDate() + 7)),
};

export default function ItineraryGenerationPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');

  const form = useForm<ItineraryFormValues>({
    resolver: zodResolver(ItinerarySchema),
    defaultValues: {
      preferences: '',
      skillLevel: 'beginner',
      duration: 3,
      location: 'Cairngorms National Park',
      date: new Date(),
    },
  });

  const onSubmit = async (data: ItineraryFormValues) => {
    // TODO: AI generation requires a backend service
    // Option 1: Set up Firebase Cloud Functions to call the Genkit AI flows
    // Option 2: Set up a separate Node.js API server
    // For now, redirect to the pre-built itineraries
    setError('AI-powered itinerary generation requires a backend server. Please explore our pre-built itineraries in the Discover Routes section.');

    // Alternatively, you could redirect to discover page:
    // navigate('/itinerary/discover');
  };
  
  return (
    <div className="container mx-auto py-8">
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center gap-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight">AI Itinerary Planner</h1>
          <p className="text-muted-foreground mt-2">Let's plan your next wild swimming adventure.</p>
        </div>
        <Alert className="w-full">
          <Info className="h-4 w-4" />
          <AlertTitle>Backend Setup Required</AlertTitle>
          <AlertDescription>
            AI-powered itinerary generation requires a backend server (Firebase Functions or custom API)
            to run the Genkit AI flows. For now, explore our pre-built itineraries in the{' '}
            <a href="/itinerary/discover" className="underline font-medium">Discover Routes</a> section.
          </AlertDescription>
        </Alert>
        <Card className="w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader>
                <CardTitle>Trip Details</CardTitle>
                <CardDescription>Tell us what you're looking for, and our AI will do the rest.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <TripDetailsForm control={form.control} />
              </CardContent>
              <CardFooter className="flex-col items-start gap-4">
                 <div className="flex w-full flex-col sm:flex-row gap-2">
                  <SubmitButton isSubmitting={form.formState.isSubmitting} />
                </div>
                <SurpriseMeButton onSurprise={() => form.reset(surpriseValues)} />
              </CardFooter>
            </form>
          </Form>
        </Card>
          {error && (
            <p className="mt-4 text-sm font-medium text-destructive text-center">{error}</p>
          )}
      </div>
    </div>
  );
}
